import React, { useCallback, useMemo } from 'react';
import { genericMemo } from '@app/helpers';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { cn } from 'lib/utils';

export interface SelectOption<T = string> {
  label: string;
  value: T;
}

export interface SelectProps<T = unknown> {
  label: string;
  value: SelectOption<T> | null;
  placeholder?: string;
  onChange: (value: SelectOption<T> | null, name?: string) => void;
  helperText?: string;
  error?: boolean;
  options: SelectOption<T>[];
  name?: string;
  valueKey?: keyof T;
  required?: boolean;
  defaultLabel?: string;
  className?: string;
}

function Select<T = unknown>(props: SelectProps<T>) {
  const {
    label,
    value: propsValue,
    onChange,
    placeholder,
    helperText = '',
    error = false,
    options,
    name,
    valueKey = '',
    required = false,
    defaultLabel,
    className,
  } = props;

  const getOptionValue = useCallback(
    (option: SelectOption<T>) => {
      if (valueKey) {
        // ensure we always return a string representation
        const v = option.value[valueKey as keyof T];
        return v === null || typeof v === 'undefined' ? '' : String(v);
      }

      return option.value === null || typeof option.value === 'undefined'
        ? ''
        : String(option.value);
    },
    [valueKey]
  );

  const onValueChange = useCallback(
    (selectedValue: string) => {
      if (!selectedValue) {
        onChange(null, name);
      } else {
        if (!valueKey) {
          onChange(
            options.find((option) => String(option.value) === selectedValue) ||
              null,
            name
          );
        } else {
          onChange(
            options.find(
              (option) =>
                String(option.value[valueKey as keyof T]) === selectedValue
            ) || null,
            name
          );
        }
      }
    },
    [name, valueKey, onChange, options]
  );

  const value = useMemo(() => {
    if (!propsValue) {
      return '';
    }

    return getOptionValue(propsValue);
  }, [getOptionValue, propsValue]);

  return (
    <div className={cn('space-y-2', className)}>
      <Label className={cn('text-sm font-medium', error && 'text-destructive')}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <ShadcnSelect value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className={cn(
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        >
          <SelectValue placeholder={defaultLabel || placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option, optionIndex) => (
            <SelectItem
              key={optionIndex.toString()}
              value={getOptionValue(option)}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </ShadcnSelect>
      {helperText && (
        <p
          className={cn(
            'text-sm text-muted-foreground',
            error && 'text-destructive'
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

export default genericMemo(Select);
